import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useReactTable, getCoreRowModel, flexRender } from "@tanstack/react-table";
import { MoreVertical, Plus, Loader, Loader2 } from "lucide-react";
import { toast } from "react-hot-toast";
import Dropdown from "../../../components/ui/Dropdown";
import { useCampaigns, useCreateCampaign, useUpdateCampaign } from "../hooks/useCampaigns";
import CampaignFormModal from "./CampaignFormModal";
import PauseAllCampaigns from "./PauseAllCampaigns";

const statusStyles = {
  DRAFT: "bg-gray-100 text-gray-800",
  ACTIVE: "bg-green-100 text-green-800",
  PAUSED: "bg-yellow-100 text-yellow-800",
  ENDED: "bg-red-100 text-red-800",
};

const CampaignsTable = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useCampaigns();
  const { mutate: createCampaign, isPending: isCreating } = useCreateCampaign();
  const { mutate: updateCampaign, isPending: isUpdating } = useUpdateCampaign();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);

  const records = useMemo(() => data?.data || [], [data]);

  const handleCreate = () => {
    setEditingCampaign(null);
    setIsFormOpen(true);
  };

  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    setIsFormOpen(true);
  };

  // The existing PAUSED status, as one click: a paused campaign disappears from the consumer app
  // (list, banner and redemption) and comes back when resumed.
  const handleSetStatus = (campaign, status) => {
    updateCampaign(
      { uuid: campaign.uuid, payload: { status } },
      {
        onSuccess: () => toast.success(status === "PAUSED" ? "Campaign paused" : "Campaign resumed"),
        onError: (err) => toast.error(err?.response?.data?.message || "Could not change the campaign"),
      }
    );
  };

  const handleSubmit = (payload) => {
    if (editingCampaign) {
      updateCampaign(
        { uuid: editingCampaign.uuid, payload },
        {
          onSuccess: () => {
            toast.success("Campaign updated successfully");
            setIsFormOpen(false);
          },
          onError: (err) =>
            toast.error(err?.response?.data?.message || "Failed to update campaign"),
        }
      );
    } else {
      createCampaign(payload, {
        onSuccess: () => {
          toast.success("Campaign created successfully");
          setIsFormOpen(false);
        },
        onError: (err) => toast.error(err?.response?.data?.message || "Failed to create campaign"),
      });
    }
  };

  const columns = [
    { accessorKey: "name", header: "Name" },
    {
      id: "reward",
      header: "Reward",
      cell: ({ row }) => `${row.original.rewardCurrency} ${Number(row.original.rewardAmount).toLocaleString()}`,
    },
    { accessorKey: "code", header: "Code", cell: (info) => info.getValue() || "—" },
    {
      accessorKey: "status",
      header: "Status",
      cell: (info) => {
        const status = info.getValue();
        return (
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusStyles[status] || "bg-gray-100 text-gray-800"}`}>
            {status}
          </span>
        );
      },
    },
    {
      id: "redemptions",
      header: "Redemptions",
      cell: ({ row }) =>
        `${row.original.currentRedemptions ?? 0}${row.original.maxRedemptions ? ` / ${row.original.maxRedemptions}` : ""}`,
    },
    {
      accessorKey: "endDate",
      header: "End Date",
      cell: (info) => (info.getValue() ? new Date(info.getValue()).toLocaleDateString() : "—"),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <Dropdown
          trigger={<MoreVertical className="w-4 h-4" />}
          items={[
            { label: "View Details", onClick: () => navigate(`/coupon-campaigns/${row.original.uuid}`) },
            { label: "Edit", onClick: () => handleEdit(row.original) },
            ...(row.original.status === "ACTIVE"
              ? [{ label: "Pause", onClick: () => handleSetStatus(row.original, "PAUSED") }]
              : []),
            ...(row.original.status === "PAUSED"
              ? [{ label: "Resume", onClick: () => handleSetStatus(row.original, "ACTIVE") }]
              : []),
          ]}
        />
      ),
    },
  ];

  const table = useReactTable({ data: records, columns, getCoreRowModel: getCoreRowModel() });

  if (isError) {
    return (
      <div className="flex flex-1 justify-center items-center h-48">
        <p className="px-6 py-4 text-red-600 bg-red-50 rounded-xl border border-red-100">
          Error: {error.message}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <PauseAllCampaigns />
      <div className="flex justify-end pb-5 bg-white">
        <button
          onClick={handleCreate}
          className="flex gap-2 items-center px-5 py-2.5 text-sm font-bold text-white bg-[#013941] rounded-xl transition-all hover:bg-[#012B36] active:scale-95"
        >
          <Plus className="w-4 h-4" /> New Campaign
        </button>
      </div>

      {isLoading ? (
        <div className="flex h-[400px]">
          <div className="flex flex-1 justify-center items-center">
            <p className="flex gap-2 items-center font-medium text-gray-600">
              Loading campaigns <Loader className="w-5 h-5 animate-spin" />
            </p>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded border border-gray-200">
          <div className="overflow-x-auto min-h-[400px]">
            <table className="w-full">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className="bg-gray-50/50">
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className="px-4 py-4 text-xs font-bold tracking-wider text-left text-gray-500 uppercase border-b md:py-4 md:px-6"
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-gray-100">
                {records.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className="transition-colors hover:bg-gray-50">
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className="px-4 py-3 font-sans text-sm text-gray-600 md:px-6 md:py-5">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <p className="text-lg font-medium">No campaigns yet</p>
                        <p className="text-sm">Click "New Campaign" to create one</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <CampaignFormModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleSubmit}
        isLoading={isCreating || isUpdating}
        campaign={editingCampaign}
      />
    </div>
  );
};

export default CampaignsTable;
